import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { FreezeTemperature } from './freezeUi';

// Ice falls while the subscription goes on hold; embers rise while it comes back.
const PARTICLE = {
  ice: { fill: '215,242,255', glow: 'rgba(170,225,255,0.8)', rises: false },
  warm: { fill: '255,190,130', glow: 'rgba(255,170,100,0.8)', rises: true }
} as const;

type Particle = { x: number; y: number; r: number; v: number; a: number; ph: number };

type Props = {
  temperature?: FreezeTemperature;
  /** Particles per 100k px² of surface, so a tall hero is not emptier than a short one. */
  density?: number;
};

/** Canvas snowfall for the surface it is dropped into; the parent must position itself. Painted, since
 *  a few dozen drifting DOM elements made the page stutter on a mid-range laptop. */
export default function FreezeSnowfall({ temperature = 'ice', density = 26 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const tone = PARTICLE[temperature];
    let particles: Particle[] = [];
    let width = 0;
    let height = 0;
    let frame = 0;
    let visible = true;

    const measure = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = parent.clientWidth;
      height = parent.clientHeight;
      canvas.width = Math.max(1, Math.round(width * ratio));
      canvas.height = Math.max(1, Math.round(height * ratio));
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = Math.round(((width * height) / 100000) * density);
      particles = Array.from({ length: Math.max(12, count) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 0.7 + Math.random() * 1.7,
        v: 0.14 + Math.random() * 0.42,
        a: 0.25 + Math.random() * 0.5,
        ph: Math.random() * Math.PI * 2
      }));
    };

    const tick = (time: number) => {
      frame = requestAnimationFrame(tick);
      if (!visible || !width || !height) return;
      context.clearRect(0, 0, width, height);
      context.shadowBlur = 6;
      context.shadowColor = tone.glow;
      particles.forEach((p) => {
        p.y += tone.rises ? -p.v * 0.8 : p.v;
        p.x += Math.sin(time / 1400 + p.ph) * 0.35;
        if (tone.rises ? p.y < -5 : p.y > height + 5) {
          p.y = tone.rises ? height + 5 : -5;
          p.x = Math.random() * width;
        }
        if (p.x < -5) p.x = width + 5;
        if (p.x > width + 5) p.x = -5;
        const twinkle = 0.75 + 0.25 * Math.sin(time / 700 + p.ph);
        context.beginPath();
        context.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        context.fillStyle = `rgba(${tone.fill},${p.a * twinkle})`;
        context.fill();
      });
    };

    measure();
    frame = requestAnimationFrame(tick);

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(parent);
    // Nothing is painted while the surface is scrolled away.
    const intersectionObserver = new IntersectionObserver((entries) => entries.forEach((e) => (visible = e.isIntersecting)), {
      threshold: 0
    });
    intersectionObserver.observe(parent);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [density, temperature]);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        mixBlendMode: 'screen',
        borderRadius: 'inherit',
        zIndex: 0
      }}
    />
  );
}
